import { ArrowUpRight } from "lucide-react";
import { MARKETS, useTickMarkets, fmt, fmtChange, seededSeries } from "@/lib/market";
import Sparkline from "./Sparkline";
import { Reveal, Eyebrow } from "./Reveal";
import { useLaunchModal } from "./LaunchModal";

export default function Markets() {
    const quotes = useTickMarkets();
    const { open } = useLaunchModal();

    return (
        <section id="markets" data-testid="markets-section" className="relative bg-void py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <Reveal>
                            <Eyebrow>Market Overview</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="max-w-2xl font-grotesk text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                                Markets move.
                                <br />
                                <span className="text-grad-blue">Tavonx moves with them.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
                                Explore the digital assets shaping the next generation of finance.
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2} className="shrink-0">
                        <a
                            href="#markets"
                            onClick={(e) => {
                                e.preventDefault();
                                open("trading");
                            }}
                            data-testid="markets-view-all-btn"
                            className="inline-flex items-center gap-2 rounded border border-white/15 px-6 py-3 font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-cyber/60 hover:text-cyber"
                        >
                            View All Markets
                            <ArrowUpRight className="h-4 w-4" />
                        </a>
                    </Reveal>
                </div>

                <div className="no-scrollbar mt-14 flex snap-x gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible xl:grid-cols-5">
                    {MARKETS.map((m, i) => {
                        const q = quotes[m.symbol];
                        return (
                            <Reveal
                                key={m.symbol}
                                delay={i * 0.07}
                                className="min-w-[250px] snap-start md:min-w-0"
                            >
                                <div
                                    data-testid={`market-card-${m.symbol.split("/")[0].toLowerCase()}`}
                                    className="group relative h-full overflow-hidden rounded-lg border border-white/[0.07] bg-panel/50 p-5 transition-all duration-500 hover:-translate-y-1.5 hover:border-cyber/40 hover:shadow-[0_16px_44px_rgba(0,209,255,0.09)]"
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="font-grotesk text-sm font-semibold tracking-wide text-white">
                                                {m.symbol.replace("/", " / ")}
                                            </div>
                                            <div className="mt-1 text-xs text-slate-500">{m.name}</div>
                                        </div>
                                        <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/[0.03]">
                                            <span className="font-mono text-[10px] text-cyber">{m.symbol.slice(0, 1)}</span>
                                        </span>
                                    </div>
                                    <div className="mt-5 flex items-end justify-between gap-2">
                                        <span
                                            className={`font-mono text-lg font-medium tabular-nums transition-colors duration-300 ${
                                                q.dir >= 0 ? "text-bull" : "text-bear"
                                            }`}
                                        >
                                            ${fmt(q.price, m.decimals)}
                                        </span>
                                        <span
                                            className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${
                                                q.change >= 0
                                                    ? "bg-bull/10 text-bull"
                                                    : "bg-bear/10 text-bear"
                                            }`}
                                        >
                                            {fmtChange(q.change)}
                                        </span>
                                    </div>
                                    <Sparkline
                                        data={seededSeries(m.seed, 28)}
                                        className="mt-4 h-12 w-full"
                                        stroke={q.change >= 0 ? "#10B981" : "#EF4444"}
                                    />
                                    <div className="mt-4 flex items-center justify-between border-t border-white/[0.06] pt-4">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-600">
                                            Demo data
                                        </span>
                                        <span className="inline-flex items-center gap-1 font-grotesk text-xs font-medium uppercase tracking-[0.16em] text-slate-400 transition-colors duration-300 group-hover:text-cyber">
                                            Trade
                                            <ArrowUpRight className="h-3.5 w-3.5" />
                                        </span>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                    Simulated market data for illustration only — not indicative of live market conditions.
                </p>
            </div>
        </section>
    );
}
